import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from "../utils/dateUtils";
import { deleteTimesheet } from '../redux/actions/timesheetActions';
import { TIMESHEET_DELETE_RESET } from '../redux/constants/timesheetConstants';
import axios from '../utils/axiosConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  Container,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { smallTitle } from '../styles/typography';
import { CircularProgress } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import ShadDatePicker from "./ShadDatePicker";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";


const tableStyles = windowsTableStyles();
const TimeSheetTable = () => {
  const { t } = useTranslation(['common', 'buttons', 'pages']);
  const { id } = useParams(); // Assuming the case ID is passed as a URL parameter
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Use local state for case-specific timesheets to avoid conflicts with global list
  const [caseTimesheets, setCaseTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const { success: deleteSuccess, error: deleteError, message: deleteMessage } = useSelector((state) => state.timesheetDelete);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filteredTimesheets, setFilteredTimesheets] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredTimesheetId, setHoveredTimesheetId] = useState(null);
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // State for notification snackbar
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // State to track which buttons show the tick icon
  const [tickButtons, setTickButtons] = useState({});

  // Function to handle the animation for row buttons
  const handleCloneWithAnimation = (timesheetId, timesheetDate) => {
    // Show tick icon
    setTickButtons(prev => ({ ...prev, [timesheetId]: true }));
    
    // Navigate to create timesheet with the date
    const dateParam = `&date=${new Date(timesheetDate).toISOString().split('T')[0]}`;
    navigate(`/timesheets/create?caseId=${id}${dateParam}`);
    
    // Revert back to plus icon after 1.5 seconds
    setTimeout(() => {
      setTickButtons(prev => ({ ...prev, [timesheetId]: false }));
    }, 1500);
  };



  // Fetch timesheets directly using axios to avoid Redux conflicts
  useEffect(() => {
    const fetchCaseTimesheets = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/timesheets/case/${id}`);
        setCaseTimesheets(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCaseTimesheets();
    }
  }, [id]);

  useEffect(() => {
    if (caseTimesheets) {
      console.log("timesheets", caseTimesheets);
    }
  }, [caseTimesheets]);

  // Handle delete success or error
  useEffect(() => {
    if (deleteSuccess && !isDeleting) {
      setNotification({
        open: true,
        message: deleteMessage || 'Timesheet deleted successfully',
        severity: 'success'
      });
    } else if (deleteError && !isDeleting) {
      setNotification({
        open: true,
        message: deleteError,
        severity: 'error'
      });
    }
  }, [deleteSuccess, deleteError, deleteMessage, isDeleting]);

  const handleViewTimesheet = (timesheetId) => {
    const currentFile = files.find(file => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate(`/timesheets/${timesheetId}`, { state: { caseId: id, owner } });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const truncateText = (text, maxLength) => {
    if (text?.length <= maxLength) {
      return text;
    }
    return text?.substring(0, maxLength) + '...';
  };

  const userFiles = files?.filter((file) => {
    if (user?.role === "Manager") {
      return true; // Managers can see all files
    } else if (user?.role === "Responsible Lawyer") {
      return file.officeMembers?.some(
        (member) => member.member._id === user.relationship && member.role === "Responsible Lawyer"
      );
    } else if (user?.role === "Assistant Lawyer") {
      return file.officeMembers?.some(
        (member) => member.member._id === user.relationship && member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  useEffect(() => {
    if (caseTimesheets) {
      let filtered = caseTimesheets.filter(
        (timesheet) => id === timesheet.fileCase?._id
      );

      if (selectedDate) {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);
        filtered = filtered.filter((ts) => {
          const tsDate = new Date(ts.date);
          return tsDate >= startOfDay && tsDate <= endOfDay;
        });
      }

      // Sort by date descending (newest first), then by createdAt (newest first) for same-day entries
      const sorted = filtered.sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        
        // Compare dates first
        if (aDate.getTime() !== bDate.getTime()) {
          return bDate.getTime() - aDate.getTime(); // Newest first
        }
        
        // If dates are the same, sort by createdAt (newest first)
        // Fallback to ObjectId timestamp if createdAt doesn't exist
        let aCreated = 0;
        let bCreated = 0;
        
        if (a.createdAt) {
          aCreated = new Date(a.createdAt).getTime();
        } else if (a._id) {
          // Extract timestamp from ObjectId (first 4 bytes represent timestamp)
          const objectIdString = a._id.toString();
          const timestampHex = objectIdString.substring(0, 8);
          aCreated = parseInt(timestampHex, 16) * 1000; // Convert to milliseconds
        }
        
        if (b.createdAt) {
          bCreated = new Date(b.createdAt).getTime();
        } else if (b._id) {
          // Extract timestamp from ObjectId (first 4 bytes represent timestamp)
          const objectIdString = b._id.toString();
          const timestampHex = objectIdString.substring(0, 8);
          bCreated = parseInt(timestampHex, 16) * 1000; // Convert to milliseconds
        }
        
        return bCreated - aCreated; // Newest first
      });
      setFilteredTimesheets(sorted);
      setPage(0);
    }
  }, [caseTimesheets, id, selectedDate]);

  const handleChangeDateByOffset = (offsetDays) => {
    let baseDate = selectedDate ? new Date(selectedDate) : new Date();
    baseDate.setHours(0, 0, 0, 0);
    baseDate.setDate(baseDate.getDate() + offsetDays);
    setSelectedDate(baseDate);
  };

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
  };

  const handleClearDateFilter = () => {
    setSelectedDate(null);
  };

  const calculateTotalTime = (timesheets) => {
    let totalMinutes = 0;
  
    (timesheets || []).forEach((timesheet) => {
      if (timesheet?.totalDuration && typeof timesheet.totalDuration === 'string') {
        try {
          const [hours, minutes] = timesheet.totalDuration.split(":").map(Number);
          if (!isNaN(hours) && !isNaN(minutes)) {
            totalMinutes += hours * 60 + minutes;
          }
        } catch (error) {
          // Skip invalid duration
        }
      }
    });
  
    // Format as minutes if less than 60
    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    } else {
      const totalHours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      
      if (remainingMinutes === 0) {
        return totalHours === 1 ? `${totalHours} hour` : `${totalHours} hours`;
      } else {
        return totalHours === 1 
          ? `${totalHours} hour ${remainingMinutes} min` 
          : `${totalHours} hours ${remainingMinutes} min`;
      }
    }
  };
  
  const calculateTotalAmount = (timesheets) => {
    return (timesheets || []).reduce((sum, timesheet) => sum + (timesheet?.totalAmount || 0), 0).toFixed(2);
  };

  const convertToDecimalHours = (time) => {
    if (!time || typeof time !== 'string') return "0h 0m";
    
    try {
      const [hours, minutes, seconds] = time.split(":").map(Number);
      
      if (isNaN(hours) || isNaN(minutes)) return "0h 0m";
      
      if (hours === 0 && minutes === 0) {
        return "0h 0m";
      } else if (hours === 0) {
        return `${minutes}m`;
      } else if (minutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${minutes}m`;
      }
    } catch (error) {
      return "0h 0m";
    }
  };

  // Handle opening delete confirmation dialog
  const handleDeleteClick = (timesheet) => {
    setTimesheetToDelete(timesheet);
    setDeleteDialogOpen(true);
  };

  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (timesheetToDelete) {
      setIsDeleting(true);
      const success = await dispatch(deleteTimesheet(timesheetToDelete._id));
      
      // Refresh the list after deletion completes
      setTimeout(async () => {
        try {
          const response = await axios.get(`/api/timesheets/case/${id}`);
          setCaseTimesheets(response.data);
        } catch (err) {
          console.error('Error refetching timesheets:', err);
        }
        setIsDeleting(false);
      }, 1000);
    }
    setDeleteDialogOpen(false);
    setTimesheetToDelete(null);
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTimesheetToDelete(null);
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
    // Reset the delete state
    dispatch({ type: TIMESHEET_DELETE_RESET });
  };

  const formatDateLocal = (dateString) => formatDate(dateString);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const stringToColor = (string) => {
    if (!string) return "#ccc"; // Default gray color if string is empty

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }

    return color;
  };


  console.log("checking the timesheet",filteredTimesheets)


  return (
    <Box sx={{ backgroundColor: 'white', padding: "32px 24px " }}>
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('pages:timesheets.delete_timesheet')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('pages:timesheets.delete_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:secondary.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('buttons:danger.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            {t('common:filter_by_date')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleChangeDateByOffset(-1)}
            sx={{ padding: "4px" }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <ShadDatePicker
            label={t('common:date')}
            value={selectedDate}
            onChange={handleDateChange}
            width={160}
            align="end"
          />
          <IconButton
            size="small"
            onClick={() => handleChangeDateByOffset(1)}
            sx={{ padding: "4px" }}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
          <Button
            variant="text"
            size="small"
            onClick={() => handleChangeDateByOffset(0)}
            sx={{ textTransform: "none" }}
          >
            {t('common:today')}
          </Button>
          {selectedDate && (
            <Button
              variant="text"
              size="small"
              onClick={handleClearDateFilter}
              sx={{ textTransform: "none" }}
            >
              {t('common:clear')}
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer>
        {filteredTimesheets.length > 0 ? (
          <Table sx={{ ...tableStyles.container, border: "1px solid #d1d1d1" }}
            size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>Type</TableCell>
                <TableCell sx={tableStyles.headerCell}>Owner</TableCell>
                <TableCell sx={tableStyles.headerCell}>Date</TableCell>
                <TableCell sx={tableStyles.headerCell}>Description</TableCell>
                <TableCell sx={tableStyles.headerCell}>Status</TableCell>
                <TableCell sx={tableStyles.headerCell}>Client</TableCell>
                <TableCell sx={tableStyles.headerCell}>Time</TableCell>
                <TableCell sx={tableStyles.headerCell}>Total Amount</TableCell>
                <TableCell sx={tableStyles.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTimesheets
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((timesheet) => (
                <TableRow 
                  key={timesheet._id} 
                  onMouseEnter={() => setHoveredTimesheetId(timesheet._id)}
                  onMouseLeave={() => setHoveredTimesheetId(null)}
                  sx={{ ...tableStyles.row, "&:hover": { backgroundColor: "#ecf5fb" } }}
                >
                  <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", lineHeight: "12px" }} onClick={() => handleViewTimesheet(timesheet._id)}>{truncateText(timesheet.type, 50)}</TableCell>
                  <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} 
                  // onClick={() => { navigate(`/relationships/${timesheet?.owner?._id}`); }}
                  >
                    {timesheet.owner ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          alt={`${timesheet.owner.firstName || ""} ${timesheet.owner.lastName || ""}`}
                          src={timesheet.owner.avatar ? timesheet.owner.avatar : "/static/images/avatar/1.jpg"} // Default avatar if none is provided
                          sx={{
                            width: "1rem", // Adjust size of the avatar
                            height: "1rem",
                            fontSize: "8px",
                            // backgroundColor: stringToColor(`${timesheet.owner.firstName} ${timesheet.owner.lastName}`), // Random color based on name
                          }}
                        >
                          {!timesheet.owner.avatar && timesheet.owner.firstName && timesheet.owner.lastName &&
                            `${timesheet.owner.firstName.charAt(0)}${timesheet.owner.middleName ? timesheet.owner.middleName.charAt(0) : timesheet.owner.lastName.charAt(0)}`}
                        </Avatar>
                        <span>
                          {`${timesheet.owner.firstName || ""} ${timesheet.owner.middleName ? timesheet.owner.middleName + " " : ""}${timesheet.owner.lastName || ""}`}
                        </span>
                      </Box>
                    ) : (
                      "No owner"
                    )}
                  </TableCell>


                  <TableCell
                    sx={{
                      ...tableStyles.cell,
                      '&:hover': {
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      },
                      color: '#005b92',
                    }}
                    onClick={() => handleViewTimesheet(timesheet._id)}
                  >
                    {formatDateLocal(timesheet.date)}
                  </TableCell>
                  <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", lineHeight: "12px" }} onClick={() => handleViewTimesheet(timesheet._id)}>{truncateText(timesheet.description, 50)}</TableCell>
                  
                  <TableCell sx={tableStyles.cell}>
                    <span
                      style={{
                        padding: "1px 6px",
                        borderRadius: "20px",
                        backgroundColor: timesheet.isBilled ? "#85bc20" : "#e5202e", // Green for Billed, Red for Not Billed
                        color: timesheet.isBilled ? "black" : "white",
                        fontSize: "8.25px",
                        fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                        fontWeight: "700",
                        textTransform: "upperCase",
                        height: "12px",
                        letterSpacing: "0.2em"

                      }}
                    >
                      {timesheet.isBilled ? t('pages:timesheets.status.billed') : t('pages:timesheets.status.not_billed')}
                    </span>
                  </TableCell>

                  
                  <TableCell
                    sx={{
                      ...tableStyles.cell,

                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Avatar
                        alt={
                          timesheet?.fileCase?.parties?.find((party) => party.type === "Client")
                            ?.name?.businessName || "N/A"
                        }
                        src={
                          timesheet?.fileCase?.parties?.find((party) => party.type === "Client")
                            ?.name?.avatar || "/static/images/avatar/1.jpg"
                        }
                        style={{
                          width: "16px",
                          height: "16px",
                          marginRight: "3px"
                        }}
                      />
                      <Box
                        sx={{
                          display: "block",
                          fontFamily:
                            '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                          fontSize: "10.5px",
                          fontWeight: 400,
                          height: "13.5px",
                          lineHeight: "13.5px",
                          marginLeft: "-3px",
                        }}
                      >
                        {(() => {
                          const client = timesheet?.fileCase?.parties?.find(
                            (party) => party.type === "Client"
                          )?.name;

                          return client
                            ? client.businessName || `${client.firstName || ""} ${client.lastName || ""}`.trim()
                            : "N/A";
                        })()}
                      </Box>
                    </Box>
                  </TableCell>


                  <TableCell sx={tableStyles.cell}>{convertToDecimalHours(timesheet.totalDuration)}</TableCell>
                  <TableCell sx={tableStyles.cell}>€ {Number(timesheet.totalAmount || 0).toFixed(2).toString().replace('.', ',')}</TableCell>
                  <TableCell sx={tableStyles.cell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {hoveredTimesheetId === timesheet._id && (
                      <Tooltip title="Delete">
                          <IconButton 
                            onClick={() => handleDeleteClick(timesheet)} 
                            size="small"
                            sx={{
                              padding: "2px",
                              color: "rgb(229, 32, 46)",
                              "&:hover": {
                                backgroundColor: "rgba(229, 32, 46, 0.1)",
                              },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Tooltip>
                      )}
                      <Tooltip title="Clone timesheet with this date">
                        <IconButton 
                          onClick={() => handleCloneWithAnimation(timesheet._id, timesheet.date)} 
                          color="primary" 
                          size="small"
                        >
                          {tickButtons[timesheet._id] ? (
                            <CheckIcon fontSize="small" sx={{ color: 'green' }} />
                          ) : (
                            <AddIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={6} sx={{...tableStyles, fontWeight: "bold", fontSize: "12px"}}>
                  {t('common:total')}
                </TableCell>
                <TableCell sx={{...tableStyles, fontWeight: "bold", padding: "2px 8px"}}>
                  {calculateTotalTime(filteredTimesheets)}
                </TableCell>
                <TableCell sx={{...tableStyles, fontWeight: "bold", padding: "2px 8px"}}>
                  € {calculateTotalAmount(filteredTimesheets).replace(".", ",")}
                </TableCell>
                <TableCell sx={tableStyles.cell}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <Box
            sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
          >
            {t('common:no_data_available')}
          </Box>
        )}
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredTimesheets.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default TimeSheetTable;